import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PaymentDetail } from '@/modules/payments/services/payments.service';
import { PaymentProviderBadge } from '@/modules/payments/components/PaymentProviderBadge';
import { PaymentStatusBadge } from '@/modules/payments/components/PaymentStatusBadge';
import { PortalStaggerItem } from '@/modules/portal/components/ui/portal-motion';
import { downloadPortalPaymentReceipt } from '@/modules/portal/services/portalPayments.api';
import type { PortalOverview } from '@/modules/portal/types/portalOverview';
import {
  canDownloadPortalReceipt,
  formatPortalPaymentDate,
  formatPortalPaymentMethod,
  formatPortalProviderReference,
  resolvePortalPaymentContext,
} from '@/modules/portal/utils/portalPaymentsHelpers';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { useTenant } from '@/providers/TenantProvider';
import { formatMoney } from '@/shared/utils/money';
import { cn } from '@/shared/lib/cn';

interface PortalPaymentCardProps {
  payment: PaymentDetail;
  overview?: PortalOverview | undefined;
  locale: string;
}

export function PortalPaymentCard({ payment, overview, locale }: PortalPaymentCardProps) {
  const { t } = useTranslation();
  const { activeTenantId } = useTenant();
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState(false);

  const formatProvider = (provider: string) =>
    t(`payments.providers.${provider}`, { defaultValue: provider });

  const { productName, planName } = resolvePortalPaymentContext(payment, overview);
  const providerRef = formatPortalProviderReference(payment);
  const paymentMethod = formatPortalPaymentMethod(payment, formatProvider);
  const showReceipt = canDownloadPortalReceipt(payment);

  const handleReceipt = async () => {
    if (!activeTenantId || !showReceipt) return;
    setReceiptError(false);
    setReceiptLoading(true);
    try {
      await downloadPortalPaymentReceipt(payment.id, activeTenantId);
    } catch {
      setReceiptError(true);
    } finally {
      setReceiptLoading(false);
    }
  };

  const subscriptionLabel =
    productName || planName ? [productName, planName].filter(Boolean).join(' · ') : null;

  return (
    <PortalStaggerItem>
      <article
        className={cn(
          'portal-glass-panel flex flex-col gap-4 rounded-2xl p-5 sm:p-6',
          'border border-border/60',
        )}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <p className="text-sm text-muted-foreground">{t('portal.payments.fields.date')}</p>
            <p className="text-base font-semibold text-foreground tabular-nums">
              {formatPortalPaymentDate(payment, locale)}
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <PaymentStatusBadge status={payment.status} />
            {payment.provider ? (
              <PaymentProviderBadge provider={payment.provider} label={payment.provider} />
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-2xl font-bold tracking-tight text-foreground tabular-nums">
            {formatMoney(payment.amount, payment.currency, locale === 'ar-SA' ? 'ar' : 'en')}
          </span>
          <span className="text-sm font-medium text-muted-foreground uppercase">
            {payment.currency}
          </span>
        </div>

        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          {paymentMethod ? (
            <div className="min-w-0">
              <dt className="text-muted-foreground">{t('portal.payments.fields.method')}</dt>
              <dd className="mt-0.5 font-medium text-foreground">{paymentMethod}</dd>
            </div>
          ) : null}
          {subscriptionLabel ? (
            <div className="min-w-0">
              <dt className="text-muted-foreground">{t('portal.payments.fields.subscription')}</dt>
              <dd className="mt-0.5 font-medium text-foreground">{subscriptionLabel}</dd>
            </div>
          ) : null}
          {providerRef ? (
            <div className="min-w-0 sm:col-span-2">
              <dt className="text-muted-foreground">{t('portal.payments.fields.reference')}</dt>
              <dd className="mt-0.5 break-all font-mono text-xs text-foreground">{providerRef}</dd>
            </div>
          ) : null}
        </dl>

        {showReceipt ? (
          <div className="flex flex-col gap-2 border-t border-border/50 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">{t('portal.payments.receipt.hint')}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={receiptLoading || !activeTenantId}
              onClick={() => void handleReceipt()}
              className="gap-2 self-start sm:self-auto"
            >
              {receiptLoading ? (
                <Icons.RefreshCw
                  className="h-4 w-4 animate-spin motion-reduce:animate-pulse"
                  aria-hidden
                />
              ) : (
                <Icons.Download className="h-4 w-4" aria-hidden />
              )}
              {t('portal.payments.receipt.download')}
            </Button>
          </div>
        ) : null}

        {receiptError ? (
          <p className="text-xs text-destructive" role="alert">
            {t('portal.payments.receipt.error')}
          </p>
        ) : null}
      </article>
    </PortalStaggerItem>
  );
}
